import React from "react";

export interface GifData {
  id: string;
  title: string;
  url: string;
  preview: string;
}

interface GiphyResultsProps {
  gifs: GifData[];
  query?: string;
  isDarkMode?: boolean;
}

export const GiphyResults: React.FC<GiphyResultsProps> = ({ gifs, query = "GIFs", isDarkMode = false }) => {
  const wrapperClasses = isDarkMode
    ? "bg-transparent border border-slate-800 text-slate-100"
    : "bg-transparent border border-slate-200 text-slate-900";
  const headerClasses = isDarkMode
    ? "bg-slate-900/60 border-slate-800/80"
    : "bg-slate-50 border-slate-200";
  const cardClasses = isDarkMode
    ? "bg-slate-900/50 border-slate-800/70 text-slate-200"
    : "bg-white border-slate-200 text-slate-700";
  const captionClasses = isDarkMode ? "text-slate-300" : "text-slate-600";

  const openImageModal = (gif: GifData) => {
    try {
      const event = new CustomEvent("openFullscreenImageModal", {
        detail: { images: [{ url: gif.url, description: gif.title || "GIF" }], initialIndex: 0 },
      });
      window.dispatchEvent(event);
    } catch (e) {
      console.error("Failed to open fullscreen image modal:", e);
    }
  };

  if (!gifs || gifs.length === 0) {
    const emptyStateClasses = isDarkMode
      ? "bg-rose-500/10 border border-rose-500/40 text-rose-200"
      : "bg-rose-50 border border-rose-200 text-rose-600";
    return (
      <div className={`${emptyStateClasses} rounded-2xl px-6 py-10 text-center shadow-lg`}>
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-500/20 text-2xl">🎬</div>
        <h3 className="mt-4 text-lg font-semibold">No GIFs found</h3>
        <p className="mt-2 text-sm opacity-80">We couldn't find any GIFs for "{query}". Try another search term!</p>
      </div>
    );
  }

  return (
    <div className={`${wrapperClasses} border-0 sm:border-border shadow-none`}>
      {/* <div className={`${headerClasses} rounded-2xl border border-1 flex flex-col gap-2 border-b px-6 py-4 sm:flex-row sm:items-center sm:justify-between`}>
        <div>
          <p className="text-[11px] font-semibold tracking-[0.35em] uppercase opacity-70">
            <span className="text-xs font-medium opacity-60">{gifs.length} </span>
            Gif Results
          </p>
        </div>
      </div> */}
      <div className="grid gap-2 bg-transparent py-4 px-0 sm:grid-cols-3 lg:grid-cols-4">
        {gifs.map((gif) => (
          <button
            key={gif.id}
            type="button"
            className={`group block overflow-hidden rounded-2xl border ${cardClasses} transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl text-left`}
            onClick={() => openImageModal(gif)}
          >
            <div className="relative aspect-video w-full overflow-hidden">
              {/* Use preview for faster loading, fall back to full url */}
              <img
                src={gif.preview || gif.url}
                alt={gif.title || "GIF"}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement;
                  // fallback to full url if preview fails
                  if (target.src !== gif.url) {
                    target.src = gif.url;
                  } else {
                    target.style.display = "none";
                  }
                }}
              />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default GiphyResults;
